import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cow, Eye, EyeSlash, Trophy, Shuffle, ImageSquare, Note } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function BingoAdmin() {
  const [enabled, setEnabled] = useState(null);     // null = loading
  const [entries, setEntries] = useState([]);
  const [totalCards, setTotalCards] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [view, setView] = useState("photos");      // "photos" | "notes" | "leaderboard"
  const [lightbox, setLightbox] = useState(null);   // photo item being previewed
  const [toggleDialog, setToggleDialog] = useState(false);
  const [drawDialog, setDrawDialog] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [winner, setWinner] = useState(null);

  const load = async () => {
    try {
      const [cfg, ents, subs, lead] = await Promise.all([
        api.get("/bingo/config"),
        api.get("/admin/bingo/entries"),
        api.get("/admin/bingo/submissions"),
        api.get("/admin/bingo/leaderboard"),
      ]);
      setEnabled(!!cfg.data.enabled);
      setEntries(ents.data.entries || []);
      setTotalCards(ents.data.total_cards || 0);
      setPhotos(subs.data.photos || []);
      setNotes(subs.data.notes || []);
      setLeaderboard(lead.data.leaderboard || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    try {
      await api.post("/admin/bingo/enable", { enabled: !enabled });
      toast.success(!enabled ? "Coo Bingo is now LIVE for delegates" : "Bingo hidden from delegates");
      setToggleDialog(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const draw = async () => {
    setDrawing(true);
    try {
      const { data } = await api.post("/admin/bingo/draw");
      setWinner(data);
      setDrawDialog(false);
      toast.success(`Winner: ${data.winner_name}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDrawing(false); }
  };

  if (enabled === null) return <div className="py-8 text-center text-[#7A828C]">Loading…</div>;

  return (
    <div data-testid="bingo-admin" className="space-y-5">
      {/* Publish banner - same pattern as QuizAdmin */}
      <div
        data-testid="bingo-publish-banner"
        className={`p-4 border rounded-sm flex items-center gap-4 flex-wrap ${enabled ? "border-[#A8D5BA] bg-[#EAF7EE]" : "border-[#E8D58A] bg-[#FFF8E5]"}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-mono-time text-[10px] uppercase tracking-widest ${enabled ? "text-[#1f6b3a]" : "text-[#7a5a00]"}`}>
              {enabled ? "Live to delegates" : "Hidden from delegates"}
            </span>
            <span className={`inline-block w-2 h-2 rounded-full ${enabled ? "bg-[#1f6b3a]" : "bg-[#b08a00]"}`} aria-hidden="true" />
          </div>
          <p className="text-sm text-[#1c1c24] leading-snug">
            {enabled
              ? "Coo Bingo CTA is showing on the Info tab and delegates can play."
              : "Bingo is hidden. Use the admin Preview link below to test, then publish when you're ready."}
          </p>
        </div>
        <Button
          onClick={() => setToggleDialog(true)}
          className={`rounded-sm ${enabled ? "bg-[#7A828C] text-[#FFFFFF] hover:bg-[#5a626c]" : "bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"}`}
          data-testid="bingo-publish-toggle"
        >
          {enabled ? <><EyeSlash size={14} className="mr-1" /> Hide</> : <><Eye size={14} className="mr-1" /> Publish bingo</>}
        </Button>
        <a
          href="/bingo?preview=1"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center h-10 px-3 rounded-sm border border-[#000000] text-[#000000] hover:bg-[#E6F8FC] text-sm font-medium transition-colors"
          data-testid="bingo-admin-preview-link"
        >
          <Eye size={14} className="mr-1.5" /> Preview as delegate
        </a>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Cards in play" value={totalCards} testid="bingo-stat-cards" />
        <Stat label="Full-house entries" value={entries.length} testid="bingo-stat-entries" />
        <Stat label="Photos submitted" value={photos.length} testid="bingo-stat-photos" />
        <Stat label="Social squares" value={notes.length} testid="bingo-stat-notes" />
      </div>

      {/* Draw */}
      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C] mb-0.5">Prize draw</div>
            <h3 className="font-display text-lg">Pick a winner</h3>
            <p className="text-xs text-[#5a5a5a] mt-1">Random pick from {entries.length} full-house {entries.length === 1 ? "entry" : "entries"}. Best run after the closing session.</p>
          </div>
          <Button
            onClick={() => setDrawDialog(true)}
            disabled={entries.length === 0}
            className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24] flex-shrink-0"
            data-testid="bingo-draw-btn"
          >
            <Shuffle size={14} className="mr-1.5" /> Draw winner
          </Button>
        </div>
        {winner && (
          <div className="p-3 bg-[#E6F8FC] border border-[#000000]/20 rounded-sm flex items-center gap-3" data-testid="bingo-winner-card">
            <Trophy size={24} weight="duotone" className="text-[#000000]" />
            <div className="min-w-0 flex-1">
              <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#000000]">Winner</div>
              <div className="font-display text-lg leading-tight">{winner.winner_name}</div>
              <div className="text-xs text-[#5a5a5a]">{winner.winner_email}</div>
            </div>
          </div>
        )}
      </div>

      {/* Entries list */}
      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <h3 className="font-display text-lg mb-2 flex items-center gap-2"><Cow size={20} weight="duotone" className="text-[#000000]" /> Full-house entries</h3>
        {entries.length === 0 ? (
          <p className="text-sm text-[#7A828C] py-4 text-center">No full houses yet.</p>
        ) : (
          <ul className="divide-y divide-[#E5E9F0]">
            {entries.map((e) => (
              <li key={e.user_id} className="py-2 flex items-center justify-between gap-2" data-testid={`bingo-entry-${e.user_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{e.name || "(no name)"}</div>
                  <div className="text-xs text-[#7A828C] truncate">{e.email}</div>
                </div>
                <div className="text-[10px] text-[#7A828C] font-mono-time flex-shrink-0">
                  {(e.full_house_at || "").slice(11, 16)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Submissions explorer: photos / notes / leaderboard */}
      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h3 className="font-display text-lg flex items-center gap-2">
            <Cow size={20} weight="duotone" className="text-[#000000]" />
            Delegate submissions
          </h3>
          <div className="inline-flex border border-[#E5E9F0] rounded-sm overflow-hidden text-xs">
            <button
              onClick={() => setView("photos")}
              data-testid="bingo-view-photos"
              className={`px-3 h-9 flex items-center gap-1.5 ${view === "photos" ? "bg-[#000000] text-[#FFFFFF]" : "bg-[#FFFFFF] text-[#1c1c24] hover:bg-[#F5F7FA]"}`}
            >
              <ImageSquare size={14} /> Photos ({photos.length})
            </button>
            <button
              onClick={() => setView("notes")}
              data-testid="bingo-view-notes"
              className={`px-3 h-9 flex items-center gap-1.5 border-l border-[#E5E9F0] ${view === "notes" ? "bg-[#000000] text-[#FFFFFF]" : "bg-[#FFFFFF] text-[#1c1c24] hover:bg-[#F5F7FA]"}`}
            >
              <Note size={14} /> Notes ({notes.length})
            </button>
            <button
              onClick={() => setView("leaderboard")}
              data-testid="bingo-view-leaderboard"
              className={`px-3 h-9 flex items-center gap-1.5 border-l border-[#E5E9F0] ${view === "leaderboard" ? "bg-[#000000] text-[#FFFFFF]" : "bg-[#FFFFFF] text-[#1c1c24] hover:bg-[#F5F7FA]"}`}
            >
              <Trophy size={14} /> Leaderboard ({leaderboard.length})
            </button>
          </div>
        </div>

        {view === "photos" && (
          photos.length === 0 ? (
            <p className="text-sm text-[#7A828C] py-8 text-center">No photo submissions yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="bingo-photo-grid">
              {photos.map((p, i) => (
                <PhotoTile key={`${p.user_id}-${p.square_id}-${i}`} photo={p} idx={i} onOpen={setLightbox} />
              ))}
            </div>
          )
        )}

        {view === "notes" && (
          notes.length === 0 ? (
            <p className="text-sm text-[#7A828C] py-8 text-center">No social-square notes yet.</p>
          ) : (
            <ul className="divide-y divide-[#E5E9F0]" data-testid="bingo-notes-list">
              {notes.map((n, i) => (
                <li key={`${n.user_id}-${i}`} className="py-3" data-testid={`bingo-note-${i}`}>
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <span className="text-xs text-[#7A828C] font-mono-time uppercase tracking-widest truncate">{n.square_text}</span>
                    <span className="text-[10px] text-[#7A828C] font-mono-time flex-shrink-0">
                      {(n.completed_at || "").slice(11, 16)}
                    </span>
                  </div>
                  <div className="text-sm text-[#1c1c24] mb-1">{n.note || <span className="text-[#7A828C] italic">(no note)</span>}</div>
                  <div className="text-xs text-[#7A828C]">{n.user_name} · {n.user_email}</div>
                </li>
              ))}
            </ul>
          )
        )}

        {view === "leaderboard" && (
          leaderboard.length === 0 ? (
            <p className="text-sm text-[#7A828C] py-8 text-center">No cards yet.</p>
          ) : (
            <ul className="divide-y divide-[#E5E9F0]" data-testid="bingo-leaderboard">
              {leaderboard.map((row, idx) => (
                <li key={row.user_id} className="py-2 flex items-center gap-3" data-testid={`bingo-leader-${idx}`}>
                  <div className="w-7 text-center font-mono-time text-xs text-[#7A828C]">{idx + 1}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      {row.name || "(no name)"}
                      {row.full_house && <Trophy size={12} weight="fill" className="text-[#b08a00]" />}
                    </div>
                    <div className="text-xs text-[#7A828C] truncate">{row.email}</div>
                  </div>
                  <div className="text-sm font-mono-time text-[#1c1c24] flex-shrink-0">
                    {row.done}<span className="text-[#7A828C]">/{row.total}</span>
                  </div>
                  <div className="w-20 h-1.5 rounded-full bg-[#E5E9F0] overflow-hidden flex-shrink-0">
                    <div className="h-full bg-[#000000]" style={{ width: `${(row.done / row.total) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )
        )}
      </div>

      {/* Photo lightbox */}
      {lightbox && (
        <PhotoLightbox photo={lightbox} onClose={() => setLightbox(null)} />
      )}

      {/* Toggle confirmation */}
      <AlertDialog open={toggleDialog} onOpenChange={setToggleDialog}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E5E9F0]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">
              {enabled ? "Hide Coo Bingo from delegates?" : "Publish Coo Bingo to delegates?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#5a5a5a] leading-relaxed">
              {enabled
                ? "The CTA on the Info tab will disappear and delegates will no longer be able to open the bingo page. Existing cards are kept."
                : "The CTA card will appear on the Info tab and every delegate gets a 5x5 bingo card on first visit. Best to publish at the conference opening."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={toggle}
              className={`rounded-sm ${enabled ? "bg-[#a83232] hover:bg-[#8a2929]" : "bg-[#000000] hover:bg-[#1c1c24]"} text-[#FFFFFF]`}
              data-testid="bingo-publish-confirm"
            >
              {enabled ? "Hide bingo" : "Publish now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Draw confirmation */}
      <AlertDialog open={drawDialog} onOpenChange={setDrawDialog}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E5E9F0]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Pick a winner from {entries.length} entries?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#5a5a5a]">
              This picks a random entrant. You can re-draw if needed - each draw is logged in the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={draw} disabled={drawing} className="rounded-sm bg-[#000000] hover:bg-[#1c1c24] text-[#FFFFFF]">
              {drawing ? "Drawing…" : "Pick winner"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm" data-testid={testid}>
      <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">{label}</div>
      <div className="font-display text-2xl text-[#1c1c24] mt-0.5">{value}</div>
    </div>
  );
}

// Each photo tile self-fetches its own base64 data URL on mount so the list
// response stays small (we used to inline every photo and hit Cloudflare's
// response-size cap with a 520 error).
function PhotoTile({ photo, idx, onOpen }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    api.get(`/admin/bingo/photo/${photo.user_id}/${photo.square_id}`)
      .then((r) => { if (active) setSrc(r.data.photo_url || ""); })
      .catch(() => { if (active) setErr(true); });
    return () => { active = false; };
  }, [photo.user_id, photo.square_id]);
  return (
    <button
      onClick={() => src && onOpen({ ...photo, photo_url: src })}
      disabled={!src}
      className="text-left bg-[#000000]/5 border border-[#E5E9F0] rounded-sm overflow-hidden hover:border-[#000000] transition-colors disabled:opacity-70"
      data-testid={`bingo-photo-${idx}`}
    >
      <div className="aspect-square bg-[#000000]/10 grid place-items-center">
        {src ? (
          <img src={src} alt={photo.square_text} className="w-full h-full object-cover" />
        ) : err ? (
          <span className="text-[10px] text-[#a83232] font-mono-time">Failed to load</span>
        ) : (
          <span className="text-[10px] text-[#7A828C] font-mono-time">Loading…</span>
        )}
      </div>
      <div className="p-2">
        <div className="text-xs text-[#1c1c24] line-clamp-2 leading-tight">{photo.square_text}</div>
        <div className="text-[10px] text-[#7A828C] truncate mt-1">{photo.user_name}</div>
      </div>
    </button>
  );
}

function PhotoLightbox({ photo, onClose }) {
  const [src, setSrc] = useState(photo.photo_url || null);
  useEffect(() => {
    if (photo.photo_url) { setSrc(photo.photo_url); return; }
    api.get(`/admin/bingo/photo/${photo.user_id}/${photo.square_id}`)
      .then((r) => setSrc(r.data.photo_url || ""))
      .catch(() => setSrc(""));
  }, [photo]);
  return (
    <div
      onClick={onClose}
      data-testid="bingo-photo-lightbox"
      className="fixed inset-0 z-50 bg-[#000000]/90 grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-w-3xl w-full max-h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
        {src
          ? <img src={src} alt={photo.square_text} className="max-w-full max-h-[75vh] object-contain rounded-sm bg-[#000000]/40" />
          : <div className="h-[40vh] grid place-items-center text-[#FFFFFF] font-mono-time text-xs">Loading photo…</div>}
        <div className="mt-3 p-3 bg-[#FFFFFF] rounded-sm">
          <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">{photo.square_text}</div>
          <div className="font-display text-base mt-0.5">{photo.user_name}</div>
          <div className="text-xs text-[#7A828C]">{photo.user_email} · {photo.completed_at ? new Date(photo.completed_at).toLocaleString("en-GB") : ""}</div>
        </div>
        <button onClick={onClose} data-testid="bingo-photo-lightbox-close" className="mt-3 self-center text-[#FFFFFF] text-xs font-mono-time uppercase tracking-widest hover:underline">
          Close
        </button>
      </div>
    </div>
  );
}
